import React, { useState, useEffect } from 'react';
import {
  Box, Heading, Container, Text, Button, Flex, VStack, Divider, Grid, GridItem,
  Alert, AlertIcon, useToast, Tooltip, HStack, Badge,
  IconButton, useBreakpointValue, Image, Input, Icon
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import { setCart } from '../features/cart/cartSlice';
import Loader from '../components/Loader';
import axios from 'axios';
import { AddIcon, EditIcon, DeleteIcon, PhoneIcon, CheckIcon } from '@chakra-ui/icons';
import { FaTruck, FaShieldAlt, FaBox } from 'react-icons/fa';
import { useLocation, Navigate, useNavigate } from 'react-router-dom';
import { createRazorpayOrder, verifyPayment } from '../services/orderService';

const MotionBox = motion(Box);
const MotionButton = motion(Button);

const CheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const cart = useSelector(state => state.cart);
  const dispatch = useDispatch();
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const [profile, setProfile] = useState({ fullName: '', profiles: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProfileIndex, setSelectedProfileIndex] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('razorpay'); // Only 'razorpay'
  const [orderNotes, setOrderNotes] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentSuccessInProgress, setPaymentSuccessInProgress] = useState(false);

  // Coupon states
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [showCouponList, setShowCouponList] = useState(false);

  // Coupon states
  // const [availableCoupons, setAvailableCoupons] = useState([]);
  // const [loadingCoupons, setLoadingCoupons] = useState(false);
  // const [couponCode, setCouponCode] = useState('');
  // const [appliedCoupon, setAppliedCoupon] = useState(null);
  // const [couponDiscount, setCouponDiscount] = useState(null);
  // const [validatingCoupon, setValidatingCoupon] = useState(false);
  // const [couponError, setCouponError] = useState('');
  // const [showCouponList, setShowCouponList] = useState(false);

  // Generate a unique key for this checkout session based on cart items
  const getCheckoutSessionKey = () => {
    const cartItemIds = cart.items.map(item => item.id).sort().join('-');
    const userId = profile.fullName || 'guest'; // Use user identifier
    return `checkout_notes_${userId}_${cartItemIds}`;
  };

  // Load order notes from localStorage when component mounts
  useEffect(() => {
    if (cart.items.length > 0) {
      const sessionKey = getCheckoutSessionKey();
      const savedNotes = localStorage.getItem(sessionKey);
      if (savedNotes) {
        setOrderNotes(savedNotes);
      }
    }
  }, [cart.items, profile.fullName]);

  // Save order notes to localStorage when they change
  useEffect(() => {
    if (cart.items.length > 0 && orderNotes !== undefined) {
      const sessionKey = getCheckoutSessionKey();
      if (orderNotes.trim()) {
        localStorage.setItem(sessionKey, orderNotes);
      } else {
        localStorage.removeItem(sessionKey);
      }
    }
  }, [orderNotes, cart.items, profile.fullName]);

  useEffect(() => {
    if (location.state?.from !== 'cart' && cart.items.length === 0) return;

    const fetchCheckoutData = async () => {
      try {
        setLoading(true);
        const [profileResponse, cartResponse] = await Promise.all([
          axios.get('/api/auth/profile/'),
          axios.get('/api/cart/')
        ]);

        const profilesData = profileResponse.data;
        const userProfile = {
          fullName: profilesData[0]?.full_name || '',
          profiles: profilesData || [],
        };
        setProfile(userProfile);
        dispatch(setCart(cartResponse.data));

        // Auto-select the default profile or first profile if available
        if (profilesData.length > 0) {
          const defaultIndex = profilesData.findIndex(p => p.isDefault === true);
          setSelectedProfileIndex(defaultIndex >= 0 ? defaultIndex : 0);
        }

      } catch (err) {
        const errorMessage = err.response?.data?.detail || 'Failed to fetch checkout information.';
        setError(errorMessage);
        toast({
          title: 'Error',
          description: errorMessage,
          status: 'error',
          duration: 5000,
          isClosable: true
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCheckoutData();
    fetchAvailableCoupons();
  }, [location.state, dispatch, cart.items.length]);

  // Fetch available coupons
  const fetchAvailableCoupons = async () => {
    try {
      setLoadingCoupons(true);
      const response = await axios.get('/api/coupons/');
      setAvailableCoupons(response.data.coupons || []);
    } catch (err) {
      console.error('Error fetching coupons:', err);
      // Silent fail - coupons are optional
    } finally {
      setLoadingCoupons(false);
    }
  };

  // Fetch available coupons
  // const fetchAvailableCoupons = async () => {
  //   try {
  //     setLoadingCoupons(true);
  //     const response = await axios.get('/api/cart-checkout/coupons/');
  //     setAvailableCoupons(response.data.coupons || []);
  //   } catch (err) {
  //     console.error('Error fetching coupons:', err);
  //     // Silent fail - coupons are optional
  //   } finally {
  //     setLoadingCoupons(false);
  //   }
  // };

  // Handle new address from address management page
  useEffect(() => {
    if (location.state?.from === 'address-management' && location.state?.newProfile) {
      const newProfile = location.state.newProfile;
      if (location.state.isNew) {
        // Add new profile and auto-select it
        setProfile(prev => ({
          ...prev,
          profiles: [...prev.profiles, newProfile]
        }));
        setSelectedProfileIndex(profile.profiles.length);
      } else {
        // Profile was edited, refresh the data by re-fetching
        const fetchUpdatedData = async () => {
          try {
            const profileResponse = await axios.get('/api/auth/profile/');
            const profilesData = profileResponse.data;
            const userProfile = {
              fullName: profilesData[0]?.full_name || '',
              profiles: profilesData || [],
            };
            setProfile(userProfile);

            // Maintain selected profile if possible
            if (profilesData.length > 0) {
              const defaultIndex = profilesData.findIndex(p => p.isDefault === true);
              setSelectedProfileIndex(defaultIndex >= 0 ? defaultIndex : 0);
            }
          } catch (err) {
            console.error('Error refreshing profile data:', err);
          }
        };
        fetchUpdatedData();
      }
    }
  }, [location.state]);

  const selectedProfile = profile.profiles[selectedProfileIndex] || null;

  const handleDeleteProfile = async (profileIndex) => {
    try {
      const profileToDelete = profile.profiles[profileIndex];
      await axios.delete(`/api/auth/profile/delete/${profileToDelete.id}/`);

      const updatedProfiles = profile.profiles.filter((_, index) => index !== profileIndex);
      setProfile(prev => ({ ...prev, profiles: updatedProfiles }));

      // Adjust selected profile index if necessary
      if (selectedProfileIndex >= updatedProfiles.length) {
        setSelectedProfileIndex(Math.max(0, updatedProfiles.length - 1));
      }

      toast({
        title: 'Address Deleted',
        description: 'Address has been removed successfully',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || 'Failed to delete address';
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    }
  };

  const navigateToAddressManagement = (mode, profileData = null) => {
    navigate('/address-management', {
      state: {
        mode,
        profile: profileData,
        fullName: profile.fullName
      }
    });
  };

  // Validate and apply coupon
  const handleApplyCoupon = async (code) => {
    if (!code || !code.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setValidatingCoupon(true);
    setCouponError('');

    try {
      const response = await axios.post('/api/cart/validate-coupon/', {
        coupon_code: code.trim().toUpperCase(),
        cart_total: parseFloat(cart.total)
      });

      if (response.data.valid) {
        setAppliedCoupon(response.data);
        setCouponDiscount(response.data);
        setCouponCode(code.trim().toUpperCase());
        setShowCouponList(false);
        toast({
          title: 'Coupon Applied!',
          description: response.data.message || `You save Rs ${response.data.discount_amount}`,
          status: 'success',
          duration: 4000,
          isClosable: true
        });
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Invalid coupon code';
      setCouponError(errorMsg);
      setAppliedCoupon(null);
      setCouponDiscount(null);
      toast({
        title: 'Invalid Coupon',
        description: errorMsg,
        status: 'error',
        duration: 4000,
        isClosable: true
      });
    } finally {
      setValidatingCoupon(false);
    }
  };

  // Remove applied coupon
  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponDiscount(null);
    setCouponCode('');
    setCouponError('');
    toast({
      title: 'Coupon Removed',
      description: 'Coupon has been removed from your order',
      status: 'info',
      duration: 3000,
      isClosable: true
    });
  };

  const handlePlaceOrder = async () => {
    if (!isOrderPlaceable) {
      toast({
        title: 'Cannot Place Order',
        description: 'Please select a delivery address',
        status: 'warning',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    // Only Razorpay payment is supported
    await handleRazorpayPayment();
  };

  const handleRazorpayPayment = async () => {
    setIsProcessingPayment(true);
    
    try {
      // Step 1: Create Razorpay order
      const orderData = {
        user_profile_id: selectedProfile.id,
        payment_method: 'razorpay',
        order_notes: orderNotes
      };

      // Include coupon code if applied
      if (appliedCoupon && couponCode) {
        orderData.coupon_code = couponCode;
      }

      const response = await createRazorpayOrder(orderData);
      const { order, razorpayKeyId } = response;

      // Step 2: Open Razorpay checkout
      const options = {
        key: razorpayKeyId || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: Math.round(parseFloat(order.amount) * 100), // Convert to paise
        currency: order.currency,
        name: 'AGNIR',
        description: `Order #${order.orderId.substring(0, 8)}`,
        order_id: order.razorpayOrderId,
        handler: async function (razorpayResponse) {
          // Step 3: Verify payment on backend
          await handlePaymentVerification(
            razorpayResponse.razorpay_order_id,
            razorpayResponse.razorpay_payment_id,
            razorpayResponse.razorpay_signature,
            order.orderId
          );
        },
        prefill: {
          name: selectedProfile.deliveringTo,
          contact: selectedProfile.phoneNumber,
          email: '' // Add if available from user data
        },
        notes: {
          order_notes: orderNotes
        },
        theme: {
          color: '#000000' // Black theme to match design
        },
        modal: {
          ondismiss: function() {
            setIsProcessingPayment(false);
            toast({
              title: 'Payment Cancelled',
              description: 'You cancelled the payment process',
              status: 'info',
              duration: 3000,
              isClosable: true
            });
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error) {
      setIsProcessingPayment(false);
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || 'Failed to initiate payment. Please try again.';
      toast({
        title: 'Payment Initiation Failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  const handlePaymentVerification = async (razorpayOrderId, razorpayPaymentId, razorpaySignature, orderId) => {
    try {
      const paymentData = {
        razorpay_order_id: razorpayOrderId,
        razorpay_payment_id: razorpayPaymentId,
        razorpay_signature: razorpaySignature,
        order_id: orderId
      };

      const response = await verifyPayment(paymentData);
      const { order } = response;

      // Set flag to prevent cart redirect during success flow
      setPaymentSuccessInProgress(true);
      setIsProcessingPayment(false);

      toast({
        title: 'Payment Successful! 🎉',
        description: 'Your order has been confirmed',
        status: 'success',
        duration: 5000,
        isClosable: true
      });

      // Clear order notes from localStorage for this session
      const sessionKey = getCheckoutSessionKey();
      localStorage.removeItem(sessionKey);

      // Clear cart and redirect to order details
      dispatch(setCart({ items: [], total: 0 }));
      navigate(`/orders/${order.orderId}`, {
        state: {
          orderSuccess: true,
          orderDetails: order
        },
        replace: true // Use replace to prevent back button issues
      });

    } catch (error) {
      setIsProcessingPayment(false);
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || 'Payment verification failed. Please contact support.';
      toast({
        title: 'Payment Verification Failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  // Prevent redirect to cart if payment success is in progress or if accessed properly from cart
  if (location.state?.from !== 'cart' && cart.items.length === 0 && !paymentSuccessInProgress) {
    return <Navigate to="/cart" replace />;
  }

  if (loading) return <Loader minH="60vh" />;
  if (error) return <Container maxW="container.xl" py={10}><Alert status="error" borderRadius="md"><AlertIcon />{error}</Alert></Container>;

  const isOrderPlaceable = selectedProfile && selectedProfile.phoneNumber;

  return (
    <>
      <MotionBox bgImage={`url(${process.env.REACT_APP_BG_URL})`} minH="100vh" py={{ base: 8, md: 12 }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        <Container maxW="container.2xl" px={{ base: 4, md: 8 }}>
          <Grid templateColumns={{ base: '1fr', lg: '1.5fr 1fr' }} gap={{ base: 8, lg: 12 }}>

            <GridItem as={VStack} spacing={8} align="stretch">
              {/* Shipping Information */}
              <Box bg="white" p={{ base: 5, md: 8 }} borderRadius="xl" boxShadow="md">
                <Heading 
                  as="h2" 
                  size="lg" 
                  mb={1}
                  fontFamily="'Cormorant', serif"
                  fontWeight="700"
                  fontSize={{ base: "3xl", md: "4xl" }}
                  letterSpacing="wide"
                  color="gray.800"
                >
                  Shipping Information
                </Heading>
                <Text 
                  color="gray.600" 
                  mb={6}
                  fontFamily="'Zain', sans-serif"
                  fontWeight="500"
                  fontSize={{ base: "md", md: "lg" }}
                >
                  Hi <Text as="span" fontWeight="700" fontFamily="'Zain', sans-serif">{profile.fullName}</Text>!
                  {selectedProfile ? " Here's your delivery information:" : " Please add a delivery address."}
                </Text>

                {selectedProfile ? (
                  <Box
                    p={6}
                    bg={selectedProfile.isDefault ? "gray.50" : "gray.50"}
                    borderRadius="lg"
                    borderWidth="2px"
                    borderColor={selectedProfile.isDefault ? "black" : "black"}
                    position="relative"
                  >
                    {/* Default Badge */}
                    <Badge
                      position="absolute"
                      top={-2}
                      left={4}
                      bg="black"
                      color="white"
                      px={2}
                      py={1}
                      borderRadius="md"
                      fontSize="xs"
                      fontWeight="600"
                      fontFamily="'Zain', sans-serif"
                    >
                      {selectedProfile.isDefault ? "✓ Default Address" : "Selected address"}
                    </Badge>

                    <VStack align="start" spacing={3} mt={2}>
                      {/* Delivering To */}
                      <Box>
                        <Text 
                          fontSize="md" 
                          color="gray.600" 
                          fontWeight="600"
                          fontFamily="'Zain', sans-serif"
                        >
                          Delivering to
                        </Text>
                        <Text 
                          fontSize="xl" 
                          fontWeight="700" 
                          color="black"
                          fontFamily="'Cormorant', serif"
                        >
                          {selectedProfile.deliveringTo}
                        </Text>
                      </Box>

                      {/* Address */}
                      <Box>
                        <Text 
                          fontSize="md" 
                          color="gray.600" 
                          fontWeight="600"
                          fontFamily="'Zain', sans-serif"
                        >
                          Address
                        </Text>
                        <Text 
                          fontSize="md" 
                          color="gray.800" 
                          lineHeight="1.6"
                          fontFamily="'Zain', sans-serif"
                          fontWeight="500"
                        >
                          {`${selectedProfile.addressLine1}${selectedProfile.addressLine2 ? ', ' + selectedProfile.addressLine2 : ''}${selectedProfile.landmark ? ', ' + selectedProfile.landmark : ''}, ${selectedProfile.locality}, ${selectedProfile.city}, ${selectedProfile.state} - ${selectedProfile.pincode}`}
                        </Text>
                      </Box>

                      {/* Phone Number */}
                      <HStack>
                        <PhoneIcon color="black" boxSize={4} />
                        <Text 
                          fontSize="md" 
                          color="gray.800"
                          fontFamily="'Zain', sans-serif"
                          fontWeight="600"
                        >
                          +91 {selectedProfile.phoneNumber}
                        </Text>
                      </HStack>
                    </VStack>

                    {/* Action Buttons */}
                    <Flex
                      justify="flex-end"
                      mt={4}
                      gap={3}
                      direction={{ base: "row", sm: "row" }}
                    >
                      <Button
                        size="sm"
                        variant="outline"
                        colorScheme="gray"
                        leftIcon={<EditIcon />}
                        onClick={() => navigateToAddressManagement('edit', selectedProfile)}
                        flex={{ base: 1, sm: "none" }}
                        borderColor="black"
                        borderWidth="1px"
                        color="black"
                        fontFamily="'Zain', sans-serif"
                        fontWeight="600"
                        _hover={{ bg: "black", color: "white" }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        colorScheme="red"
                        leftIcon={<DeleteIcon />}
                        borderWidth="1px"
                        fontFamily="'Zain', sans-serif"
                        fontWeight="600"
                        onClick={() => handleDeleteProfile(selectedProfileIndex)}
                        flex={{ base: 1, sm: "none" }}
                      >
                        Delete
                      </Button>
                    </Flex>
                  </Box>
                ) : (
                  <Box
                    p={8}
                    textAlign="center"
                    borderWidth="2px"
                    borderColor="gray.200"
                    borderStyle="dashed"
                    borderRadius="lg"
                    bg="gray.50"
                  >
                    <VStack spacing={4}>
                      <Text 
                        fontSize="lg" 
                        color="gray.600"
                        fontFamily="'Cormorant', serif"
                        fontWeight="700"
                      >
                        No delivery address found
                      </Text>
                      <Text 
                        fontSize="sm" 
                        color="gray.500"
                        fontFamily="'Zain', sans-serif"
                        fontWeight="500"
                      >
                        Add your first address to continue with checkout
                      </Text>
                      <Button
                        bg="black"
                        color="white"
                        leftIcon={<AddIcon />}
                        _hover={{ bg: "gray.800" }}
                        size="lg"
                        fontFamily="'Zain', sans-serif"
                        fontWeight="600"
                        onClick={() => navigateToAddressManagement('add')}
                      >
                        Add Address
                      </Button>
                    </VStack>
                  </Box>
                )}

                {/* Add New Address Button (when address exists) */}
                {selectedProfile && (
                  <Flex justify="center" mt={6}>
                    <Button
                      variant="ghost"
                      leftIcon={<AddIcon />}
                      onClick={() => navigateToAddressManagement('add')}
                      size={isMobile ? "md" : "lg"}
                      color="black"
                      fontFamily="'Zain', sans-serif"
                      fontWeight="600"
                      _hover={{ bg: "gray.100" }}
                    >
                      Add New Address
                    </Button>
                  </Flex>
                )}

                {/* Multiple Profiles Selector */}
                {profile.profiles.length > 1 && (
                  <Box mt={6}>
                    <Text 
                      fontSize="sm" 
                      color="gray.600" 
                      mb={3} 
                      fontWeight="500"
                      fontFamily="'Zain', sans-serif"
                    >
                      Or choose from {profile.profiles.length - 1} other address{profile.profiles.length > 2 ? 'es' : ''}:
                    </Text>
                    <VStack spacing={3}>
                      {profile.profiles.map((profileItem, index) => (
                        index !== selectedProfileIndex && (
                          <Box
                            key={index}
                            p={4}
                            bg="white"
                            borderWidth="1px"
                            borderColor="gray.200"
                            borderRadius="md"
                            w="full"
                            cursor="pointer"
                            _hover={{ bg: "gray.50", borderColor: "black" }}
                            onClick={() => setSelectedProfileIndex(index)}
                            position="relative"
                          >
                            {profileItem.isDefault && (
                              <Badge
                                position="absolute"
                                top={2}
                                right={2}
                                bg="black"
                                color="white"
                                fontSize="xs"
                                px={2}
                                py={1}
                                borderRadius="md"
                                fontFamily="'Zain', sans-serif"
                                fontWeight="600"
                              >
                                Default
                              </Badge>
                            )}
                            <Flex justify="space-between" align="center">
                              <VStack align="start" spacing={1} flex={1}>
                                <Text 
                                  fontSize="md" 
                                  fontWeight="700"
                                  fontFamily="'Cormorant', serif"
                                  color="gray.800"
                                >
                                  {profileItem.deliveringTo}
                                </Text>
                                <Text 
                                  fontSize="xs" 
                                  color="gray.600" 
                                  noOfLines={1}
                                  fontFamily="'Zain', sans-serif"
                                  fontWeight="500"
                                >
                                  {`${profileItem.addressLine1}, ${profileItem.locality}, ${profileItem.city}`}
                                </Text>
                                <Text 
                                  fontSize="xs" 
                                  color="gray.700"
                                  fontFamily="'Zain', sans-serif"
                                  fontWeight="600"
                                >
                                  +91 {profileItem.phoneNumber}
                                </Text>
                              </VStack>
                              <Button
                                size="xs"
                                variant="outline"
                                borderColor="black"
                                color="black"
                                fontFamily="'Zain', sans-serif"
                                fontWeight="600"
                                _hover={{ bg: "black", color: "white" }}
                              >
                                Select
                              </Button>
                            </Flex>
                          </Box>
                        )
                      ))}
                    </VStack>
                  </Box>
                )}
              </Box>

              {/* Payment Method */}
              <Box bg="white" p={{ base: 5, md: 8 }} borderRadius="xl" boxShadow="md">
                <Heading 
                  as="h2" 
                  size="lg" 
                  mb={5}
                  fontFamily="'Cormorant', serif"
                  fontWeight="700"
                  fontSize={{ base: "3xl", md: "4xl" }}
                  letterSpacing="wide"
                  color="gray.800"
                >
                  Payment Method
                </Heading>
                <VStack spacing={4} align="stretch">
                  {/* Razorpay Payment Option - Only payment method */}
                  <Box
                    p={6}
                    bg="gray.50"
                    borderRadius="lg"
                    borderWidth="2px"
                    borderColor="black"
                  >
                    <HStack justify="space-between" align="center">
                      <HStack spacing={4}>
                        <CheckIcon color="black" boxSize={6} />
                        <VStack align="start" spacing={1}>
                          <Text 
                            fontWeight="700" 
                            fontSize="lg" 
                            color="black"
                            fontFamily="'Cormorant', serif"
                          >
                            Pay with Razorpay
                          </Text>
                          <Text 
                            fontSize="sm" 
                            color="gray.600"
                            fontFamily="'Zain', sans-serif"
                            fontWeight="500"
                          >
                            Credit/Debit Card, UPI, Net Banking & More
                          </Text>
                        </VStack>
                      </HStack>
                      <Image 
                        src="https://razorpay.com/assets/razorpay-glyph.svg" 
                        alt="Razorpay" 
                        h={{ base: "50px", md: "60px" }}
                        w="auto"
                        fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='40'%3E%3Ctext x='10' y='25' font-family='Arial' font-size='18' fill='%23000'%3ERazorpay%3C/text%3E%3C/svg%3E"
                      />
                    </HStack>
                  </Box>
                </VStack>

                {/* Order Notes */}
                <Box mt={6}>
                  <Text 
                    fontWeight="600" 
                    mb={2} 
                    color="gray.800"
                    fontFamily="'Zain', sans-serif"
                  >
                    Order Notes (Optional)
                  </Text>
                  <Input
                    placeholder="Any special instructions for delivery..."
                    value={orderNotes}
                    onChange={(e) => setOrderNotes(e.target.value)}
                    maxLength={500}
                    bg="white"
                    borderColor="gray.300"
                    fontFamily="'Zain', sans-serif"
                    fontWeight="500"
                    _hover={{ borderColor: 'gray.400' }}
                    _focus={{ borderColor: 'black', boxShadow: '0 0 0 1px black' }}
                  />
                  <Text 
                    fontSize="xs" 
                    color="gray.500" 
                    mt={1}
                    fontFamily="'Zain', sans-serif"
                    fontWeight="500"
                  >
                    {orderNotes.length}/500 characters
                  </Text>
                </Box>
              </Box>
            </GridItem>

            <GridItem>
              {/* Coupon Section */}
              <Box bg="white" p={{ base: 5, md: 8 }} borderRadius="xl" boxShadow="md" mb={6}>
                <Heading 
                  as="h2" 
                  size="md" 
                  mb={4}
                  fontFamily="'Cormorant', serif"
                  fontWeight="700"
                  fontSize={{ base: "2xl", md: "3xl" }}
                  letterSpacing="wide"
                  color="gray.800"
                >
                  Apply Coupon
                </Heading>

                {/* Manual Coupon Entry */}
                <VStack spacing={3} align="stretch" mb={4}>
                  <Flex gap={2}>
                    <Input
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      disabled={appliedCoupon !== null || validatingCoupon}
                      maxLength={12}
                      textTransform="uppercase"
                      fontFamily="'Zain', sans-serif"
                      fontWeight="600"
                      fontSize="sm"
                      borderColor="gray.300"
                      _focus={{ borderColor: 'black', boxShadow: '0 0 0 1px black' }}
                      _disabled={{ bg: 'gray.100', cursor: 'not-allowed' }}
                    />
                    {!appliedCoupon ? (
                      <Button
                        onClick={() => handleApplyCoupon(couponCode)}
                        isLoading={validatingCoupon}
                        loadingText="Checking"
                        disabled={!couponCode.trim() || validatingCoupon}
                        bg="black"
                        color="white"
                        _hover={{ bg: 'gray.800' }}
                        _active={{ bg: 'gray.900' }}
                        fontFamily="'Zain', sans-serif"
                        fontWeight="600"
                        fontSize="sm"
                        minW="100px"
                      >
                        Apply
                      </Button>
                    ) : (
                      <Button
                        onClick={handleRemoveCoupon}
                        bg="red.500"
                        color="white"
                        _hover={{ bg: 'red.600' }}
                        _active={{ bg: 'red.700' }}
                        fontFamily="'Zain', sans-serif"
                        fontWeight="600"
                        fontSize="sm"
                        minW="100px"
                      >
                        Remove
                      </Button>
                    )}
                  </Flex>

                  {/* Coupon Error */}
                  {couponError && (
                    <Alert status="error" borderRadius="md" py={2}>
                      <AlertIcon />
                      <Text fontSize="sm" fontFamily="'Zain', sans-serif" fontWeight="500">
                        {couponError}
                      </Text>
                    </Alert>
                  )}

                  {/* Coupon Success */}
                  {appliedCoupon && (
                    <Alert status="success" borderRadius="md" py={3} bg="green.50" borderColor="green.200" borderWidth="1px">
                      <AlertIcon color="green.500" />
                      <Box flex="1">
                        <Text fontSize="sm" fontWeight="700" fontFamily="'Zain', sans-serif" color="green.800">
                          {appliedCoupon.message || 'Coupon applied successfully!'}
                        </Text>
                        <Text fontSize="xs" fontFamily="'Zain', sans-serif" color="green.700" mt={1}>
                          {appliedCoupon.discount_percentage || appliedCoupon.discountPercentage}% off
                          {(appliedCoupon.discount_type === 'max' || appliedCoupon.discountType === 'max') && 
                           (appliedCoupon.max_discount_amount || appliedCoupon.maxDiscountAmount) && (
                            <Text as="span"> (Max Rs {appliedCoupon.max_discount_amount || appliedCoupon.maxDiscountAmount})</Text>
                          )}
                        </Text>
                      </Box>
                    </Alert>
                  )}
                </VStack>

                {/* Available Coupons List */}
                {availableCoupons.length > 0 && !appliedCoupon && (
                  <Box mt={4}>
                    <Button
                      size="sm"
                      variant="link"
                      onClick={() => setShowCouponList(!showCouponList)}
                      fontFamily="'Zain', sans-serif"
                      fontWeight="600"
                      color="black"
                      textDecoration="underline"
                      _hover={{ color: 'gray.700' }}
                      mb={3}
                    >
                      {showCouponList ? 'Hide' : 'View'} {availableCoupons.length} available coupon{availableCoupons.length > 1 ? 's' : ''}
                    </Button>

                    {showCouponList && (
                      <VStack spacing={2} align="stretch" maxH="250px" overflowY="auto" pr={1}>
                        {availableCoupons.map((coupon) => (
                          <Box
                            key={coupon.code}
                            p={4}
                            bg="gray.50"
                            borderWidth="1px"
                            borderColor="gray.300"
                            borderRadius="md"
                            cursor="pointer"
                            onClick={() => handleApplyCoupon(coupon.code)}
                            _hover={{ 
                              bg: 'gray.100',
                              borderColor: 'black',
                              transform: 'translateY(-2px)',
                              boxShadow: 'sm'
                            }}
                            transition="all 0.2s"
                          >
                            <Flex justify="space-between" align="center" mb={2}>
                              <HStack spacing={2}>
                                <Text
                                  fontSize="md"
                                  fontWeight="bold"
                                  fontFamily="monospace"
                                  letterSpacing="wide"
                                  color="black"
                                >
                                  {coupon.code}
                                </Text>
                                <Badge
                                  bg="black"
                                  color="white"
                                  px={2}
                                  py={1}
                                  borderRadius="sm"
                                  fontSize="xs"
                                  fontWeight="bold"
                                >
                                  {coupon.discountPercentage}% OFF
                                </Badge>
                              </HStack>
                            </Flex>

                            <VStack align="start" spacing={1} fontSize="xs" color="gray.600">
                              {coupon.discountType === 'max' && coupon.maxDiscountAmount && (
                                <Text fontFamily="'Zain', sans-serif" fontWeight="500">
                                  • Max discount: Rs {coupon.maxDiscountAmount}
                                </Text>
                              )}
                              {coupon.minOrderAmount > 0 && (
                                <Text fontFamily="'Zain', sans-serif" fontWeight="500">
                                  • Min order: Rs {coupon.minOrderAmount}
                                </Text>
                              )}
                              {coupon.usageLimit > 0 && (
                                <Text fontFamily="'Zain', sans-serif" fontWeight="500">
                                  • {coupon.remainingUses} use{coupon.remainingUses !== 1 ? 's' : ''} remaining
                                </Text>
                              )}
                              <Text 
                                fontFamily="'Zain', sans-serif" 
                                fontWeight="500"
                                color={coupon.daysUntilExpiry <= 2 ? 'red.600' : 'gray.600'}
                              >
                                • Expires in {coupon.daysUntilExpiry} day{coupon.daysUntilExpiry !== 1 ? 's' : ''}
                              </Text>
                            </VStack>
                          </Box>
                        ))}
                      </VStack>
                    )}
                  </Box>
                )}

                {availableCoupons.length === 0 && !loadingCoupons && (
                  <Text
                    fontSize="sm"
                    color="gray.500"
                    fontStyle="italic"
                    textAlign="center"
                    py={2}
                    fontFamily="'Zain', sans-serif"
                    fontWeight="500"
                  >
                    No coupons available at the moment
                  </Text>
                )}
              </Box>

              {/* Order Summary */}
              <Box bg="white" p={{ base: 5, md: 8 }} borderRadius="xl" boxShadow="md" position={{ lg: 'sticky' }} top={{ lg: 24 }}>
                <Heading 
                  as="h2" 
                  size="lg" 
                  mb={6} 
                  borderBottomWidth="1px" 
                  pb={4}
                  fontFamily="'Cormorant', serif"
                  fontWeight="700"
                  fontSize={{ base: "3xl", md: "4xl" }}
                  letterSpacing="wide"
                  color="gray.800"
                >
                  Order Summary
                </Heading>
                <VStack spacing={4} align="stretch" my={4} maxH="300px" overflowY="auto" pr={2}>
                  {cart.items.map(item => (
                    <Flex key={item.id} justify="space-between" align="flex-start" gap={3}>
                      <Flex align="flex-start" flex={1}>
                        <Image src={item.product.primaryImage || '/no_image_available.jpg'} alt={item.product.name} boxSize="60px" objectFit="cover" borderRadius="md" mr={3} flexShrink={0} />
                        <Box flex={1} minW={0}>
                          <Text 
                            fontWeight="700" 
                            noOfLines={2} 
                            fontFamily="'Cormorant', serif"
                            color="gray.800"
                            fontSize="md"
                            lineHeight="1.3"
                            mb={1}
                          >
                            {item.product.name}
                          </Text>
                          <Text 
                            fontSize="sm" 
                            color="gray.500"
                            fontFamily="'Zain', sans-serif"
                            fontWeight="500"
                          >
                            Qty: {item.quantity}
                          </Text>
                        </Box>
                      </Flex>
                      <Text 
                        fontWeight="700" 
                        color="gray.800"
                        fontSize="md"
                        flexShrink={0}
                      >
                        Rs {item.subtotal}
                      </Text>
                    </Flex>
                  ))}
                </VStack>
                <Divider my={4} />
                <VStack spacing={3} align="stretch">
                  <Flex justify="space-between">
                    <Text 
                      color="gray.600"
                      fontFamily="'Zain', sans-serif"
                      fontWeight="600"
                      fontSize="md"
                    >
                      Subtotal
                    </Text>
                    <Text 
                      fontWeight="700"
                      color="gray.800"
                      fontSize="md"
                    >
                      Rs {cart.total}
                    </Text>
                  </Flex>
                  {/* Coupon Discount Row */}
                  {couponDiscount && (
                    <Flex justify="space-between">
                      <Text 
                        color="green.600"
                        fontFamily="'Zain', sans-serif"
                        fontWeight="700"
                        fontSize="md"
                      >
                        Coupon Discount ({couponDiscount.discount_percentage || couponDiscount.discountPercentage}%)
                      </Text>
                      <Text 
                        fontWeight="700"
                        color="green.600"
                        fontSize="md"
                      >
                        - Rs {couponDiscount.discount_amount || couponDiscount.discountAmount}
                      </Text>
                    </Flex>
                  )}

                  <Flex justify="space-between">
                    <Text 
                      color="gray.600"
                      fontFamily="'Zain', sans-serif"
                      fontWeight="600"
                      fontSize="md"
                    >
                      Shipping
                    </Text>
                    <Text 
                      fontWeight="700"
                      color="green.600"
                      fontSize="md"
                    >
                      Free
                    </Text>
                  </Flex>
                  <Flex justify="space-between" fontWeight="bold" fontSize="xl" pt={3} borderTopWidth="1px" mt={2}>
                    <Text 
                      fontFamily="'Cormorant', serif"
                      fontWeight="700"
                      color="gray.800"
                      fontSize="xl"
                    >
                      Total
                    </Text>
                    <VStack align="end" spacing={0}>
                      {couponDiscount && (
                        <Text 
                          fontSize="sm"
                          color="gray.500"
                          textDecoration="line-through"
                          fontFamily="'Zain', sans-serif"
                        >
                          Rs {cart.total}
                        </Text>
                      )}
                      <Text 
                        fontWeight="700"
                        color={couponDiscount ? "green.600" : "black"}
                        fontSize="xl"
                      >
                        Rs {couponDiscount ? (couponDiscount.final_amount || couponDiscount.finalAmount) : cart.total}
                      </Text>
                    </VStack>
                  </Flex>

                  {/* Savings Indicator */}
                  {couponDiscount && (
                    <Box bg="green.50" p={3} borderRadius="md" borderWidth="1px" borderColor="green.200" mt={2}>
                      <Text 
                        fontSize="sm"
                        color="green.700"
                        fontWeight="700"
                        textAlign="center"
                        fontFamily="'Zain', sans-serif"
                      >
                        🎉 You're saving Rs {couponDiscount.discount_amount || couponDiscount.discountAmount}!
                      </Text>
                    </Box>
                  )}
                </VStack>
                <Tooltip label="Please select a shipping address and phone number" isDisabled={isOrderPlaceable} placement="top">
                  <Box w="full" mt={8}>
                    <MotionButton
                      bg="#16a34a"
                      color="white"
                      size="lg"
                      w="full"
                      isDisabled={!isOrderPlaceable || isProcessingPayment}
                      onClick={handlePlaceOrder}
                      isLoading={loading || isProcessingPayment}
                      loadingText={isProcessingPayment ? "Processing Payment..." : "Placing Order..."}
                      whileHover={{ scale: isOrderPlaceable ? 1.02 : 1 }}
                      whileTap={{ scale: isOrderPlaceable ? 0.98 : 1 }}
                      _hover={{ bg: isOrderPlaceable ? "gray.800" : "gray.400" }}
                      _disabled={{ bg: "gray.400", color: "gray.600" }}
                      fontWeight="600"
                      fontFamily="'Zain', sans-serif"
                      fontSize={{ base: "md", md: "lg" }}
                    >
                      {!isOrderPlaceable 
                        ? 'Select Address to Continue' 
                        : `Pay Rs ${couponDiscount ? (couponDiscount.final_amount || couponDiscount.finalAmount) : cart.total} - Razorpay`}
                    </MotionButton>
                  </Box>
                </Tooltip>

                {isOrderPlaceable && (
                  <Flex 
                    direction={{ base: "row", md: "row" }}
                    gap={{ base: 2, md: 4 }}
                    justify="center" 
                    align="center"
                    mt={4}
                    fontSize="xs" 
                    color="gray.600"
                    fontFamily="'Zain', sans-serif"
                    fontWeight="500"
                  >
                    <HStack spacing={1.5}>
                      <Icon as={FaTruck} boxSize={3.5} />
                      <Text>Free delivery</Text>
                    </HStack>
                    <Text color="gray.400" display={{ base: "none", md: "block" }}>•</Text>
                    <HStack spacing={1.5}>
                      <Icon as={FaShieldAlt} boxSize={3.5} />
                      <Text>Secure Payment</Text>
                    </HStack>
                    <Text color="gray.400" display={{ base: "none", md: "block" }}>•</Text>
                    <HStack spacing={1.5}>
                      <Icon as={FaBox} boxSize={3.5} />
                      <Text>2-5 business days</Text>
                    </HStack>
                  </Flex>
                )}
              </Box>
            </GridItem>

          </Grid>
        </Container>
      </MotionBox>
    </>
  );
};

export default CheckoutPage;
